import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { DashboardLayout, Card, FormInput, Button } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { useAuth } from '../context/AuthContext';
import { adyenOnboardingApi, projectApi, customerApi, adminApi, type AdyenAccount } from '../services/api';
import type { Customer, InvitationDetails, ProjectStatus } from '../types';
import { isSelectableCustomerId } from '../utils/customerVerification';
import { apiErrorText, isRequestTimeout } from '../utils/apiError';
import { ProjectCustomerPicker } from '../components/ProjectCustomerPicker';
import { ProjectNotesGuidance } from '../components/ProjectNotesGuidance';
import { useAdyenOnboardingPrompt } from '../hooks/useAdyenOnboardingPrompt';
import { useCustomerInvite } from '../hooks/useCustomerInvite';
import { FEATURE_PROJECT_WITHOUT_CUSTOMER } from '../constants/featureFlags';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

/** How the Beställare is given (ClickUp 869ff1egy): chosen, invited or later. */
type CustomerMode = 'pick' | 'invite' | 'later';

interface ProjectFormData {
  customerId: string;
  /** Admin only, for a project without a Beställare. */
  companyId: string;
  description: string;
  startDate: string;
  endDate: string;
  status: ProjectStatus;
  externalProjectNumber: string;
  notes: string;
}

const emptyFormData: ProjectFormData = {
  customerId: '',
  companyId: '',
  description: '',
  startDate: '',
  endDate: '',
  status: 'not_started',
  externalProjectNumber: '',
  notes: ''
};

const capabilityIsValid = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') return false;
  const capability = value as { allowed?: unknown; verificationStatus?: unknown; status?: unknown };
  return capability.allowed === true || capability.verificationStatus === 'valid' || capability.status === 'valid';
};

const hasLocalMockAdyenId = (value: string | null | undefined, prefix: string): boolean =>
  value ? new RegExp(`^${prefix}-[0-9a-f-]{20,}$`, 'i').test(value) : false;

const adyenAccountAllowsProjectCreation = (account: AdyenAccount | null): boolean => {
  if (!account) return false;
  if (
    hasLocalMockAdyenId(account.adyenAccountHolderId, 'AH') ||
    hasLocalMockAdyenId(account.adyenLegalEntityId, 'LE') ||
    hasLocalMockAdyenId(account.adyenBalanceAccountId, 'BA') ||
    hasLocalMockAdyenId(account.transferInstrumentId, 'SE')
  ) {
    return false;
  }
  const required = account.requiredCapabilities ?? [];
  return (
    account.payoutsAllowed === true &&
    required.length > 0 &&
    required.every((capability) => capabilityIsValid(account.capabilities?.[capability]))
  );
};

export function ProjectCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const showAdyenOnboardingPrompt = useAdyenOnboardingPrompt(!user?.isAdmin);
  const basePath = user?.isAdmin ? '/admin/projects' : '/projects';
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerInvitations, setCustomerInvitations] = useState<InvitationDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [nextProjectNumber, setNextProjectNumber] = useState<number>(1);
  const [formData, setFormData] = useState<ProjectFormData>(emptyFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [customerSearch, setCustomerSearch] = useState('');
  const [adyenAccount, setAdyenAccount] = useState<AdyenAccount | null>(null);
  const [isLoadingAdyen, setIsLoadingAdyen] = useState(!user?.isAdmin);
  const adyenReadyForPayments = user?.isAdmin ? true : adyenAccountAllowsProjectCreation(adyenAccount);
  // "Lägg till senare" is the default: the project can start before the
  // Beställare exists in Etappio (ClickUp 869ff1egy).
  const [customerMode, setCustomerMode] = useState<CustomerMode>(FEATURE_PROJECT_WITHOUT_CUSTOMER ? 'later' : 'pick');
  const invite = useCustomerInvite();
  const [companies, setCompanies] = useState<Array<{ id: string; name: string }>>([]);
  // Inviting is the Hantverkare's own action; Admin chooses or adds later.
  const customerModes: CustomerMode[] = user?.isAdmin ? ['pick', 'later'] : ['pick', 'invite', 'later'];
  const loadData = useCallback(async () => {
    try {
      const customersData = await (user?.isAdmin
        ? adminApi.getAllCustomers()
        : customerApi.getCustomers());
      setCustomers(customersData);
      if (!user?.isAdmin) {
        const nextNumberData = await projectApi.getNextProjectNumber();
        setNextProjectNumber(nextNumberData.nextProjectNumber);
        const account = await adyenOnboardingApi.getStatus();
        setAdyenAccount(account);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingAdyen(false);
    }
  }, [user?.isAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Pending customer invitations have no customer row yet; the picker lists them
  // as disabled options. A failure here must not block project creation.
  const loadCustomerInvitations = useCallback(async () => {
    if (user?.isAdmin) return;
    try {
      setCustomerInvitations(await customerApi.getCustomerInvitations());
    } catch (error) {
      console.error('Failed to load customer invitations:', error);
    }
  }, [user?.isAdmin]);

  useEffect(() => {
    loadCustomerInvitations();
  }, [loadCustomerInvitations]);

  // Admin creating a project without a Beställare chooses the Hantverkare.
  useEffect(() => {
    if (!user?.isAdmin || !FEATURE_PROJECT_WITHOUT_CUSTOMER) return;
    adminApi.getCompanies()
      .then(rows => setCompanies(
        rows
          .filter(row => row.company?.id)
          .map(row => ({ id: row.company!.id, name: row.company!.companyName || row.user?.email || row.company!.id }))
          .sort((a, b) => a.name.localeCompare(b.name, 'sv'))
      ))
      .catch(error => console.error('Failed to load companies:', error));
  }, [user?.isAdmin]);

  const adminCustomerId = customerMode === 'pick' ? formData.customerId : '';
  const adminCompanyId = customerMode === 'pick' ? '' : formData.companyId;
  useEffect(() => {
    if (!user?.isAdmin || (!adminCustomerId && !adminCompanyId)) return;
    projectApi
      .getNextProjectNumber(adminCustomerId || undefined, adminCompanyId || undefined)
      .then((data) => setNextProjectNumber(data.nextProjectNumber))
      .catch((error) => console.error('Failed to load next project number:', error));
  }, [adminCustomerId, adminCompanyId, user?.isAdmin]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleNotesChange = (value: string) => {
    setFormData(prev => ({ ...prev, notes: value }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (customerMode === 'pick') {
      if (!formData.customerId) {
        errors.customerId = t('validation.required');
      } else if (!isSelectableCustomerId(customers, formData.customerId)) {
        errors.customerId = t('projects.form.customerNotVerifiedError');
      }
    } else if (user?.isAdmin && !formData.companyId) {
      errors.companyId = t('validation.required');
    }

    if (!formData.description.trim()) {
      errors.description = t('validation.required');
    }

    setFormErrors(errors);
    // The invitation fields are checked with the rest of the form.
    const inviteValid = customerMode !== 'invite' || invite.validate();
    return Object.keys(errors).length === 0 && inviteValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSaving(true);
    setMessage(null);

    try {
      const newProject = await projectApi.createProject({
        ...formData,
        customerId: customerMode === 'pick' ? formData.customerId : undefined,
        companyId: user?.isAdmin && customerMode !== 'pick' ? formData.companyId : undefined
      });
      // The invitation goes out once the project exists, so it can link the
      // Beställare to it after BankID. A failed invitation keeps the project.
      if (customerMode === 'invite' && !(await invite.send(newProject.id))) {
        setMessage({ type: 'error', text: t('projectCustomer.create.inviteFailed') });
        setTimeout(() => navigate(`${basePath}/${newProject.id}`), 2500);
        return;
      }
      setMessage({ type: 'success', text: t('projects.form.saveSuccess') });
      // Straight to the new project's contract tab, with the contract wizard open
      // on contract #1 (the API creates it together with the project).
      setTimeout(() => {
        navigate(`${basePath}/${newProject.id}?tab=contract&contractNumber=1&wizard=1`);
      }, 1000);
    } catch (error) {
      console.error('Failed to create project:', error);
      // A project that ran out of time may exist anyway (ClickUp 869fgxq45).
      setMessage({
        type: 'error',
        text: isRequestTimeout(error) ? t('common.createTimedOut') : apiErrorText(t, error, 'projects.form.saveError')
      });
      setIsSaving(false);
    }
  };

  const selectedCustomer = customers.find(c => c.id === formData.customerId);

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Page Header */}
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('projectCreate.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: t('projects.title'), path: '/projects' },
            { label: t('projectCreate.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('projectCreate.description')}</p>
        </div>
        <div className="d-flex justify-content-end mt-2">
          <Link to={basePath} className="btn btn-light-brand project-create-touch">
            <i className="feather-arrow-left me-2"></i>
            {t('common.back')}
          </Link>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {showAdyenOnboardingPrompt && !isLoadingAdyen && !adyenReadyForPayments && (
        <div className="alert alert-warning d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <div className="fw-semibold">
              <i className="feather-alert-triangle me-2"></i>
              {t('projects.kycReminderTitle')}
            </div>
            <div className="small">
              {t('projects.kycReminderBody')}
            </div>
          </div>
          <Link to="/settings/company?tab=adyen" className="btn btn-sm btn-warning">
            {t('projects.kycReminderButton')}
          </Link>
        </div>
      )}

      <div className="row g-4 align-items-start">
        <div className={FEATURE_PROJECT_WITHOUT_CUSTOMER ? 'col-xl-8' : 'col-12'}>
      <Card>
        <div className="card-header">
          <h5 className="card-title mb-0">{t('projectCreate.formTitle')}</h5>
        </div>
        <form onSubmit={handleSubmit} className="project-create-form">
          <div className="card-body">
            <div className="row">
              {/* Project Number */}
              <div className="col-lg-6 mb-3">
                <label className="form-label">{t('projects.columns.projectNumber')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={user?.isAdmin && !adminCustomerId && !adminCompanyId ? '' : nextProjectNumber}
                  placeholder={user?.isAdmin ? t('projects.form.selectCustomer') : undefined}
                  disabled
                />
                <small className="text-muted">{t('projects.form.projectNumberHelp')}</small>
              </div>

              {/* External Project Number */}
              <div className="col-lg-6 mb-3">
                <FormInput
                  name="externalProjectNumber"
                  label={t('projects.form.optionalLabel', { label: t('projects.form.externalProjectNumber') })}
                  value={formData.externalProjectNumber}
                  onChange={handleInputChange}
                  maxLength={128}
                  helpText={<>{t('projects.form.externalProjectNumberHelp')}<span className="d-block fst-italic">{t('projects.form.externalProjectNumberExample')}</span></>}
                />
              </div>
            </div>

            <div className="row">
              {/* Description */}
              <div className="col-12 mb-3">
                <FormInput
                  name="description"
                  label={`${t('projects.columns.description')} *`}
                  value={formData.description}
                  onChange={handleInputChange}
                  error={formErrors.description}
                  maxLength={128}
                  helpText={<>{t('projects.form.descriptionHelp')}<span className="d-block fst-italic">{t('projects.form.descriptionExample')}</span></>}
                />
              </div>
            </div>

            {/* Beställare: choose, invite or add later (ClickUp 869ff1egy). */}
            <fieldset className="project-customer-fieldset mb-4" data-testid="project-create-customer">
              <legend className="form-label mb-1">
                {t('projects.form.customer')}{!FEATURE_PROJECT_WITHOUT_CUSTOMER && ' *'}
              </legend>
              {FEATURE_PROJECT_WITHOUT_CUSTOMER && (
                <p className="text-muted small mb-3">{t('projectCustomer.create.help')}</p>
              )}
              <div className="project-customer-choices">
                {(FEATURE_PROJECT_WITHOUT_CUSTOMER ? customerModes : (['pick'] as CustomerMode[])).map(mode => (
                  <React.Fragment key={mode}>
                    {FEATURE_PROJECT_WITHOUT_CUSTOMER && (
                      <label className={`project-customer-choice${customerMode === mode ? ' is-selected' : ''}`} data-testid={`project-customer-mode-${mode}`}>
                        <input
                          type="radio"
                          className="form-check-input"
                          name="customerMode"
                          value={mode}
                          checked={customerMode === mode}
                          onChange={() => setCustomerMode(mode)}
                        />
                        <span>
                          <span className="project-customer-choice-title">{t(`projectCustomer.create.${mode}`)}</span>
                          <span className="project-customer-choice-text">{t(`projectCustomer.create.${mode}Text`)}</span>
                        </span>
                      </label>
                    )}
                    {mode === 'pick' && customerMode === 'pick' && (
                      <div className="project-customer-choice-body">
                        <label className="form-label" htmlFor="project-customer">{t('projects.form.customer')} *</label>
                        <ProjectCustomerPicker
                          id="project-customer"
                          customers={customers}
                          invitations={customerInvitations}
                          value={formData.customerId}
                          onChange={handleInputChange}
                          search={customerSearch}
                          onSearchChange={setCustomerSearch}
                          error={formErrors.customerId}
                          customerListPath={user?.isAdmin ? '/admin/customers' : '/customers'}
                        />
                        {/* Customer Name (read-only with link) */}
                        {selectedCustomer && (
                          <div className="mt-3">
                            <label className="form-label">{t('projects.form.customerName')}</label>
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type="text"
                                className="form-control bg-light"
                                value={selectedCustomer.name}
                                disabled
                              />
                              <a
                                href={`/customers?id=${selectedCustomer.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-light-primary"
                                title={t('projects.form.viewCustomer')}
                              >
                                <i className="feather-external-link"></i>
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {mode === 'invite' && customerMode === 'invite' && (
                      <div className="project-customer-choice-body row g-3">
                        <div className="col-12 col-md-4">
                          <FormInput
                            id="project-invite-name"
                            name="customerName"
                            label={t('customerInvite.customerName')}
                            value={invite.form.customerName}
                            onChange={invite.handleChange}
                            error={invite.errors.customerName ? t(invite.errors.customerName) : undefined}
                            required
                          />
                        </div>
                        <div className="col-12 col-md-4">
                          <FormInput
                            id="project-invite-email"
                            name="customerEmail"
                            type="email"
                            label={t('customerInvite.customerEmail')}
                            value={invite.form.customerEmail}
                            onChange={invite.handleChange}
                            error={invite.errors.customerEmail ? t(invite.errors.customerEmail) : undefined}
                            required
                          />
                        </div>
                        <div className="col-12 col-md-4">
                          <FormInput
                            id="project-invite-phone"
                            type="tel"
                            name="customerPhone"
                            label={t('customerInvite.customerPhone')}
                            value={invite.form.customerPhone}
                            onChange={invite.handleChange}
                            placeholder={t('customerInvite.customerPhonePlaceholder')}
                            error={invite.errors.customerPhone ? t(invite.errors.customerPhone) : undefined}
                          />
                        </div>
                        {/* The same text for every invitation, never whether the address has an account (869fmx898). */}
                        <p className="col-12 small text-muted mb-0" data-testid="invite-existing-account-hint">{t('customerInvite.existingAccountHint')}</p>
                      </div>
                    )}
                    {mode === 'later' && customerMode === 'later' && (
                      <div className="project-customer-choice-body">
                        <div className="project-customer-later-info" role="note">
                          <i className="feather-info" aria-hidden="true"></i>
                          <span><strong>{t('projectCustomer.create.laterInfoTitle')}</strong> {t('projectCustomer.create.laterInfo')}</span>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
              {user?.isAdmin && FEATURE_PROJECT_WITHOUT_CUSTOMER && customerMode !== 'pick' && (
                <div className="mt-3">
                  <label className="form-label" htmlFor="project-company">{t('projectCustomer.create.company')} *</label>
                  <select
                    id="project-company"
                    name="companyId"
                    className={`form-select${formErrors.companyId ? ' is-invalid' : ''}`}
                    value={formData.companyId}
                    onChange={handleInputChange}
                  >
                    <option value="">{t('projectCustomer.create.companyPlaceholder')}</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                  {formErrors.companyId && <div className="invalid-feedback">{formErrors.companyId}</div>}
                </div>
              )}
            </fieldset>

            <div className="row">
              {/* Start Date */}
              <div className="col-md-6 mb-3">
                <FormInput
                  name="startDate"
                  type="date"
                  label={t('projects.form.optionalLabel', { label: t('projects.columns.startDate') })}
                  value={formData.startDate}
                  onChange={handleInputChange}
                />
              </div>

              {/* End Date */}
              <div className="col-md-6 mb-3">
                <FormInput
                  name="endDate"
                  type="date"
                  label={t('projects.form.optionalLabel', { label: t('projects.columns.endDate') })}
                  value={formData.endDate}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-12 mb-3">
                <p className="text-muted small mb-0">{t('projects.form.estimatedDateHelp')}</p>
                <p className="text-muted small fst-italic mb-0">{t('projects.form.estimatedDateExample')}</p>
              </div>
            </div>


            {/* Notes (Rich Editor) */}
            <div className="mb-4">
              <label className="form-label" id="project-notes-label">
                {t('projects.form.optionalLabel', { label: t('projects.form.notes') })}
              </label>
              <ProjectNotesGuidance id="project-notes-guidance" showWhereToFind />
              <ReactQuill
                theme="snow"
                value={formData.notes}
                onChange={handleNotesChange}
                style={{ height: '150px' }}
              />
            </div>
            {/* Spacer for Quill toolbar */}
            <div style={{ height: '45px' }}></div>
          </div>
          <div className="card-footer d-flex justify-content-end">
            <Button type="submit" className="project-create-touch" isLoading={isSaving || invite.isInviting}>
              <i className={`feather-${customerMode === 'invite' ? 'send' : 'save'} me-2`}></i>
              {customerMode === 'invite' ? t('projectCustomer.create.submitInvite') : t('projectCreate.createButton')}
            </Button>
          </div>
        </form>
      </Card>
        </div>
        {FEATURE_PROJECT_WITHOUT_CUSTOMER && (
          <div className="col-xl-4">
            <aside className="project-customer-howto" aria-labelledby="project-customer-howto-title" data-testid="project-customer-howto">
              <p className="project-customer-kicker">{t('projectCustomer.howTo.kicker')}</p>
              <h2 id="project-customer-howto-title" className="project-customer-title">{t('projectCustomer.howTo.title')}</h2>
              <ol className="project-customer-howto-steps">
                {['s1', 's2', 's3', 's4'].map((key, index) => (
                  <li key={key}>
                    <span className="project-customer-step-number" aria-hidden="true">{index + 1}</span>
                    <span>
                      <strong className="d-block">{t(`projectCustomer.howTo.${key}`)}</strong>
                      {t(`projectCustomer.howTo.${key}Text`)}
                    </span>
                  </li>
                ))}
              </ol>
            </aside>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
